import React, { useState } from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { LoginGreeting } from './LoginGreeting';
import { Users, Activity, Stethoscope, FlaskConical, Scan, Pill, Settings, LogOut, Menu, X, User as UserIcon, FileText, Clock, Baby, CreditCard, Briefcase } from 'lucide-react';
export const DashboardLayout: React.FC = () => {
  const {
    currentUser,
    logout
  } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  if (!currentUser) {
    return <Navigate to="/login" replace data-id="element-25" data-mp="wgdvj3hg5l3" />;
  }
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  const getNavItems = () => {
    const items = [];
    if (currentUser.role === 'Administrator') {
      items.push({
        name: 'System Overview',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
      items.push({
        name: 'Billing & Invoicing',
        path: '/dashboard/billing',
        icon: CreditCard
      });
      items.push({
        name: 'Pharmacy Inventory',
        path: '/dashboard/pharmacy',
        icon: Pill
      });
      items.push({
        name: 'Staff Management',
        path: '/dashboard/staff',
        icon: Settings
      });
      items.push({
        name: 'Human Resource & Payroll',
        path: '/dashboard/hr-payroll',
        icon: Briefcase
      });
      items.push({
        name: 'Activity Log',
        path: '/dashboard/activity-log',
        icon: Clock
      });
    } else if (currentUser.role === 'Clinician') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Patient Files',
        path: '/dashboard/patients',
        icon: FileText
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else if (currentUser.role === 'Nursing') {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      items.push({
        name: 'Maternity Files',
        path: '/dashboard/maternity',
        icon: Baby
      });
    } else {
      items.push({
        name: 'Dashboard',
        path: '/dashboard',
        icon: Activity
      });
      if (currentUser.role === 'Pharmacy' || currentUser.role === 'Pharmacy Manager') {
        items.push({
          name: currentUser.role === 'Pharmacy Manager' ? 'Pharmacist' : 'Pharmacy Dispensing',
          path: '/dashboard/pharmacy',
          icon: Pill
        });
      }
      if (currentUser.role === 'Reception') {
        items.push({
          name: 'Billing & Invoicing',
          path: '/dashboard/billing',
          icon: CreditCard
        });
      }
    }
    return items;
  };
  const navItems = getNavItems();
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'Administrator':
        return 'bg-red-100 text-red-800';
      case 'Clinician':
        return 'bg-blue-100 text-blue-800';
      case 'Nursing':
        return 'bg-green-100 text-green-800';
      case 'Laboratory':
        return 'bg-yellow-100 text-yellow-800';
      case 'Radiology':
        return 'bg-purple-100 text-purple-800';
      case 'Pharmacy':
      case 'Pharmacy Manager':
        return 'bg-teal-100 text-teal-800';
      case 'Reception':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="min-h-screen bg-chrome-canvas flex" data-id="element-26" data-mp="2ozj4ffz6i00v">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-40 flex md:hidden" data-id="element-27" data-mp="gqbdbdsaogmv">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-28" data-mp="gp0613zn4qr0"></div>
          <div className="chrome-sidebar relative flex-1 flex flex-col max-w-xs w-full" data-id="element-29" data-mp="7qy1iegp7fyz">
            <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-30" data-mp="br8c64ffquk6">
              <button className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-31" data-mp="bpdqup0950fk">
                <X className="h-6 w-6 text-white" data-id="element-32" data-mp="qfl3up7hpr8v" />
              </button>
            </div>
            <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto" data-id="element-33" data-mp="vexznzgca8nl">
              <div className="flex-shrink-0 flex items-center px-4" data-id="element-34" data-mp="3b6vbamay6lws">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 rounded-full bg-white object-contain" data-id="element-35" data-mp="3b5k7imhi5e5o" />
                <div className="ml-2" data-id="element-36" data-mp="33maxcr169bfk">
                  <span className="block text-base font-bold leading-tight text-white" data-id="element-37" data-mp="33pf9qbosjdhb">
                    BMED Medical Centre
                  </span>
                  <span className="chrome-label text-xs font-medium uppercase tracking-wide" data-id="element-38" data-mp="3cnra35fglpmy">
                    HIMS
                  </span>
                </div>
              </div>
              <nav className="mt-5 px-2 space-y-1" data-id="element-39" data-mp="2jzvey55jg5sa">
                {navItems.map(item => {
              const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
              return <a key={item.name} href={item.path} onClick={e => {
                e.preventDefault();
                navigate(item.path);
                setSidebarOpen(false);
              }} className={`${isActive ? 'chrome-nav-item-active' : 'chrome-nav-item'} group flex items-center px-3 py-2 text-base font-medium rounded-md`} data-id="element-40" data-mp="266nncdhgjaal">
                      <item.icon className="mr-4 flex-shrink-0 h-6 w-6" data-id="element-41" data-mp="ixdshhhelc4l" />
                      {item.name}
                    </a>;
            })}
              </nav>
            </div>
            <div className="chrome-divider flex-shrink-0 border-t p-4" data-id="element-42" data-mp="3nakefnnoiokr">
              <div className="flex items-center" data-id="element-43" data-mp="3rd77tt000hk2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20" data-id="element-44" data-mp="3rbe0sclh3vbj">
                  <UserIcon className="h-6 w-6" data-id="element-45" data-mp="87cw3bwp0n0" />
                </div>
                <div className="ml-3" data-id="element-46" data-mp="16loljyguy7ib">
                  <p className="text-base font-medium" data-id="element-47" data-mp="16osxxj4h89k2">
                    {currentUser.fullName}
                  </p>
                  <p className="chrome-label text-sm font-medium" data-id="element-48" data-mp="1lpqa3tzb7bc1">
                    {currentUser.role}
                  </p>
                </div>
              </div>
              <p className="chrome-label mt-4 text-xs" data-id="element-49" data-mp="1grf0nkcxf2uc">
                Caring · Healing · Leading
              </p>
            </div>
          </div>
        </div>}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0 print:hidden" data-id="element-50" data-mp="17y33p1bnkmfj">
        <div className="flex flex-col w-64" data-id="element-51" data-mp="1309vk2qshicm">
          <div className="chrome-sidebar flex-1 flex flex-col min-h-0" data-id="element-52" data-mp="12wvuqyxrxh7a">
            <div className="flex-1 flex flex-col overflow-y-auto pb-4" data-id="element-53" data-mp="xykmgsn4ner6">
              <div className="chrome-divider flex flex-shrink-0 items-center border-b px-4 py-5" data-id="element-54" data-mp="t23wru0a4sbz">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-10 w-10 rounded-full bg-white object-contain" data-id="element-55" data-mp="p0ioklyox284" />
                <div className="ml-3" data-id="element-56" data-mp="32m8cwy2f5zpb">
                  <span className="block text-base font-bold leading-tight text-white" data-id="element-57" data-mp="32nb4l87kg66a">
                    BMED Medical Centre
                  </span>
                  <span className="chrome-label text-xs font-medium uppercase tracking-wide" data-id="element-58" data-mp="36pob3yu50844">
                    HIMS
                  </span>
                </div>
              </div>
              <nav className="mt-6 flex-1 space-y-1 px-3" data-id="element-59" data-mp="2xtf8yceryvea">
                {navItems.map(item => {
                const isActive = location.pathname === item.path || item.path !== '/dashboard' && location.pathname.startsWith(item.path);
                return <a key={item.name} href={item.path} onClick={e => {
                  e.preventDefault();
                  navigate(item.path);
                }} className={`${isActive ? 'chrome-nav-item-active' : 'chrome-nav-item'} group flex items-center rounded-full px-4 py-2.5 text-sm font-medium`} data-id="element-60" data-mp="2ew5nkppw37ue">
                      <item.icon className="mr-3 flex-shrink-0 h-5 w-5" data-id="element-61" data-mp="ibpmm0s6egdd" />
                      {item.name}
                    </a>;
              })}
              </nav>
            </div>
            <div className="chrome-divider flex-shrink-0 border-t p-4" data-id="element-62" data-mp="r99qzilc4ss7">
              <div className="flex items-center" data-id="element-63" data-mp="15yxoe8qeb7an">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20" data-id="element-64" data-mp="161jyaf0rdyw1">
                  <UserIcon className="h-5 w-5" data-id="element-65" data-mp="1130985qvc6zg" />
                </div>
                <div className="ml-3" data-id="element-66" data-mp="1ezvm69a8qiks">
                  <p className="text-sm font-medium" data-id="element-67" data-mp="1ayttkbatx4rs">
                    {currentUser.fullName}
                  </p>
                  <p className="chrome-label mt-0.5 text-xs font-medium" data-id="element-68" data-mp="3gr8p4ssnege7">
                    {currentUser.role}
                  </p>
                </div>
              </div>
              <p className="chrome-label mt-4 text-xs" data-id="element-69" data-mp="2gr8lboi20iaj">
                Caring · Healing · Leading
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden" data-id="element-70" data-mp="27qiuz0bkbk47">
        <header className="chrome-header flex items-center justify-between gap-3 border-b border-white/10 px-3 py-3 sm:px-6 print:hidden" data-id="element-71" data-mp="27qslcp0irtaf">
          <button className="chrome-header-button inline-flex h-10 w-10 items-center justify-center rounded-md md:hidden" onClick={() => setSidebarOpen(true)} data-id="element-72" data-mp="30ey79f3b7obu">
            <span className="sr-only" data-id="element-73" data-mp="2qpejbv0qp4a4">Open sidebar</span>
            <Menu className="h-6 w-6" data-id="element-74" data-mp="2qntsafdmw3h0" />
          </button>
          <LoginGreeting user={currentUser} data-id="element-75" data-mp="2lqtm84gsb0pw" />
          <div className="ml-auto flex shrink-0 items-center gap-3" data-id="element-76" data-mp="2lq1p2p7jguxi">
            <div className="hidden text-right sm:block" data-id="element-77" data-mp="3j6qyb3ytizgx">
              <p className="text-sm font-medium leading-tight" data-id="element-78" data-mp="3eez6kq3xenp7">
                {currentUser.fullName}
              </p>
              <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getRoleColor(currentUser.role)}`} data-id="element-79" data-mp="3lqnabiitu8p5">
                {currentUser.role}
              </span>
            </div>
            <span className={`rounded-full px-2 py-1 text-xs font-medium sm:hidden ${getRoleColor(currentUser.role)}`} data-id="element-80" data-mp="3vmqdmrpf0ddo">
              {currentUser.role}
            </span>
            <button onClick={handleLogout} className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-2 text-sm font-medium text-chrome-header-ink hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-81" data-mp="8j2cljr8c3um">
              <LogOut className="mr-2 h-4 w-4" data-id="element-82" data-mp="n7q51h5s3xgc" />
              Sign out
            </button>
          </div>
        </header>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none" data-id="element-83" data-mp="s5row4b140sv">
          <div className="py-6" data-id="element-84" data-mp="w73h8hlaf04r">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-85" data-mp="2t9keqqop6075">
              <Outlet data-id="element-86" data-mp="2t7pvrhwzki5p" />
            </div>
          </div>
        </main>
      </div>
    </div>;
};